import { useEffect, useState } from 'react';
import { HoaDonMuaHangTrongNuoc, HoaDonMuaHangTrongNuocInput, HoaDonMuaHangTrongNuocResponse } from '@/types/schemas';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseHoaDonMuaHangTrongNuocReturn {
  HoaDonMuaHangTrongNuocs: HoaDonMuaHangTrongNuoc[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addHoaDonMuaHangTrongNuoc: (
    newHoaDonMuaHangTrongNuoc: HoaDonMuaHangTrongNuocInput
  ) => Promise<HoaDonMuaHangTrongNuoc>;
  updateHoaDonMuaHangTrongNuoc: (
    uuid: string,
    updatedHoaDonMuaHangTrongNuoc: HoaDonMuaHangTrongNuocInput
  ) => Promise<HoaDonMuaHangTrongNuoc>;
  deleteHoaDonMuaHangTrongNuoc: (uuid: string) => Promise<void>;
  refreshHoaDonMuaHangTrongNuocs: () => Promise<void>;
  getHoaDonMuaHangTrongNuocByCustomer: (customerId: string) => Promise<HoaDonMuaHangTrongNuoc[]>;
  getHoaDonMuaHangTrongNuocDetail: (uuid: string) => Promise<[]>;
}

/**
 * Hook for managing HoaDonMuaHangTrongNuoc (Sales Invoice) data
 *
 * This hook provides functions to fetch, create, update, and delete sales invoices and their details.
 */
export const useHoaDonMuaHangTrongNuoc = (
  initialHoaDonMuaHangTrongNuocs: HoaDonMuaHangTrongNuoc[] = []
): UseHoaDonMuaHangTrongNuocReturn => {
  const [HoaDonMuaHangTrongNuocs, setHoaDonMuaHangTrongNuocs] =
    useState<HoaDonMuaHangTrongNuoc[]>(initialHoaDonMuaHangTrongNuocs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchHoaDonMuaHangTrongNuocs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonMuaHangTrongNuocResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/`
      );
      setHoaDonMuaHangTrongNuocs(response.data.results);
    } catch (error) {
      console.error('Error fetching sales invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonMuaHangTrongNuocByCustomer = async (customerId: string): Promise<HoaDonMuaHangTrongNuoc[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonMuaHangTrongNuocResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales invoices by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addHoaDonMuaHangTrongNuoc = async (
    newHoaDonMuaHangTrongNuoc: HoaDonMuaHangTrongNuocInput
  ): Promise<HoaDonMuaHangTrongNuoc> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<HoaDonMuaHangTrongNuoc>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/`,
        newHoaDonMuaHangTrongNuoc
      );

      const addedHoaDonMuaHangTrongNuoc = response.data;
      setHoaDonMuaHangTrongNuocs(prev => [...prev, addedHoaDonMuaHangTrongNuoc]);
      return addedHoaDonMuaHangTrongNuoc;
    } catch (error) {
      console.error('Error adding sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateHoaDonMuaHangTrongNuoc = async (
    uuid: string,
    updatedHoaDonMuaHangTrongNuoc: HoaDonMuaHangTrongNuocInput
  ): Promise<HoaDonMuaHangTrongNuoc> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.patch<HoaDonMuaHangTrongNuoc>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/${uuid}/`,
        updatedHoaDonMuaHangTrongNuoc
      );

      const updatedData = response.data;
      setHoaDonMuaHangTrongNuocs(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteHoaDonMuaHangTrongNuoc = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/${uuid}/chi-tiet/`);
      setHoaDonMuaHangTrongNuocs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonMuaHangTrongNuocDetail = async (uuid: string): Promise<[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<[]>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/${uuid}/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching sales invoice detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  return {
    HoaDonMuaHangTrongNuocs,
    isLoading,
    isLoadingDetail,
    addHoaDonMuaHangTrongNuoc,
    updateHoaDonMuaHangTrongNuoc,
    deleteHoaDonMuaHangTrongNuoc,
    refreshHoaDonMuaHangTrongNuocs: fetchHoaDonMuaHangTrongNuocs,
    getHoaDonMuaHangTrongNuocByCustomer,
    getHoaDonMuaHangTrongNuocDetail
  };
};

export const fetchAllHoaDonMuaHangTrongNuoc = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<HoaDonMuaHangTrongNuoc[]> => {
  let page = 1;
  let all: HoaDonMuaHangTrongNuoc[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<HoaDonMuaHangTrongNuocResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/`,
      {
        params: {
          ...params,
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
